<script lang="ts" setup>
  import { onMounted, computed, onBeforeUnmount, useTemplateRef, ref } from 'vue';
  import { useScrollWidth } from '@storefront/storefront';

  const scroll = useScrollWidth();
  const spacer = computed(() => scroll.value + 'px');

  const visible = ref(false);
  const bar = useTemplateRef('bar');

  let processes: number = 0;
  let progress: number | null = null;

  function reset() {
    processes = 0;
    progress = null;
  }

  function clamp(n: number, min: number, max: number) {
    if (n < min) return min;
    if (n > max) return max;
    return n;
  }

  function show() {
    visible.value = true;
  }

  function hide() {
    visible.value = false;
  }

  function work() {
    setTimeout(() => {
      if (progress !== null && progress < 1) {
        increase();
        work();
      }
    }, 200);
  }

  function start() {
    if (progress === null) {
      show();
      set(0);
      work();
    }

    processes++;
  }

  function set(n: number) {
    let element = bar.value;

    n = clamp(n, 0.08, 1);
    progress = n;

    if (element) {
      if (progress !== null) {
        element.style.transform = 'translateX(' + (((-1 + progress) * 100) + '%') + ')';
        element.style.opacity = '1';

        if (progress === 1) {
          setTimeout(() => {
            element.style.opacity = '0';

            setTimeout(() => {
              element.style.transform = 'translateX(-100%)';
              progress = null;

              hide();
            }, 200);
          }, 200);
        }
      }
    }
  }

  function done() {
    processes = clamp(processes - 1, 0, Number.MAX_SAFE_INTEGER);

    if (progress !== null && processes === 0) {
      set(1);
    }
  }

  function increase(amount?: number | null | undefined) {
    let current = progress;

    if (!current) {
      return start();
    } else if (current > 1) {
      return;
    } else {
      if (typeof amount !== 'number') {
        if (current >= 0 && current < 0.2) {
          amount = 0.1;
        } else if (current >= 0.2 && current < 0.5) {
          amount = 0.04;
        } else if (current >= 0.5 && current < 0.8) {
          amount = 0.02;
        } else if (current >= 0.8 && current < 0.99) {
          amount = 0.005;
        } else {
          amount = 0;
        }
      }

      current = clamp(current + amount, 0, 0.994);

      set(current);
    }
  }

  function onVisitorStart() {
    start();
  }

  function onVisitorDone() {
    done();
  }

  function addEventListeners() {
    document.addEventListener('visit:start', onVisitorStart);
    document.addEventListener('visit:done', onVisitorDone);
  }

  function removeEventListeners() {
    document.removeEventListener('visit:start', onVisitorStart);
    document.removeEventListener('visit:done', onVisitorDone);
  }

  onMounted(() => {
    addEventListeners();
  });

  onBeforeUnmount(() => {
    removeEventListeners();
    reset();
  });
</script>
<template>
  <div id="app-progress-loader" :aria-valuetext="$t('SHOP.COMMON.ARIA.PROGRESS_BAR_LOADING')" role="progressbar">
    <div v-show="visible" class="progress-bar">
      <div ref="bar" class="bar">
        <div class="peg" />
      </div>
      <div class="spinner">
        <div class="spinner-icon" />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  :root {
    --progress-bar-color: var(--primary-color);
  }
</style>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  #app-progress-loader {
    --progress-bar-spacer: v-bind(spacer);

    display: contents;
  }

  .progress-bar {
    pointer-events: none;
    display: contents;
  }

  .progress-bar .bar {
    background: var(--progress-bar-color);
    position: fixed;
    z-index: 100000;
    top: 0;
    left: 0;
    width: calc(100% - var(--progress-bar-spacer));
    transition: all 200ms linear;
    transform: translateX(-100%);
    opacity: 0;
    height: 3px;
  }

  .progress-bar .peg {
    display: block;
    position: absolute;
    right: 0;
    width: 100px;
    height: 100%;
    box-shadow: 0 0 10px var(--progress-bar-color), 0 0 5px var(--progress-bar-color);
    opacity: 1.0;
    transform: rotate(3deg) translate(0px, -4px);
  }

  .progress-bar .spinner {
    display: block;
    position: fixed;
    z-index: 100000;
    top: 12px;
    right: calc(9px + var(--progress-bar-spacer));

    @include media.query(lg) {
      top: 4px;
      right: calc(4px + var(--progress-bar-spacer));
    }
  }

  .progress-bar .spinner-icon {
    width: 18px;
    height: 18px;
    box-sizing: border-box;
    border: solid 2px transparent;
    border-top-color: var(--progress-bar-color);
    border-left-color: var(--progress-bar-color);
    border-radius: 50%;
    animation: progress-bar-spinner 400ms linear infinite;
  }

  @keyframes progress-bar-spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
